import { Flex, Skeleton } from '@chakra-ui/react'
import React, { useState } from 'react'
import iconCopy from '~/assets/svgs/icon-copy.svg?react'
import iconThumbsDown from '~/assets/svgs/icon-thumb-down.svg?react'
import iconThumbsUp from '~/assets/svgs/icon-thumb-up.svg?react'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { useToastAlert } from '~/hooks/toast/useToastAlerts'
import { useClipboard } from '~/hooks/useClipboard'
import useCloudwatchLogger from '~/hooks/useCloudwatchLogger'
import { OverallSentimentStatus } from '~/pages/Calls/CallDetails/sections/OverallSentimentStatus'
import { textStyleKeys } from '~/themes/textStyles'
import { Size } from '~/types/ThemeTypes'

import { CallInstance } from '@mangovoice/types'
import { ApiError, VoicemailInstance } from '@mangovoice/types'

import { TRANSCRIPT_SUMMARY_NOT_AVAILABLE } from '../CallDetailsAnalyticsContent'
import { VoicemailTranscriptSection } from './VoicemailTranscriptSection'

export type CallTranscriptSummarySectionProps = {
  overallCustomerScore: number
  overallAgentScore: number
  summaryText?: string
  isLoading: boolean
  voicemail?: VoicemailInstance
  voicemailError?: ApiError
  isVoicemailLoading?: boolean
  onVoicemailClick?: () => void
  callInstance?: CallInstance
}

export const CallTranscriptSummarySection = ({
  summaryText,
  overallCustomerScore,
  overallAgentScore,
  isLoading,
  voicemail,
  voicemailError,
  isVoicemailLoading,
  onVoicemailClick,
  callInstance,
}: CallTranscriptSummarySectionProps) => {
  const { logger } = useCloudwatchLogger('CallTranscriptSummarySection')
  const { toastError, toastSuccess } = useToastAlert()
  const { copyToClipboard } = useClipboard({
    onSuccess: () => toastSuccess(' Summary copied to clipboard.'),
    onFailure: () =>
      toastError('Unable to copy the summary. Please try again.'),
  })

  const [hasClickedFeedback, setHasClickedFeedback] = useState(false)

  const hasSummary = summaryText !== TRANSCRIPT_SUMMARY_NOT_AVAILABLE

  const handleThumbsUp = () => {
    toastSuccess('Thank you for your feedback!')
    if (hasClickedFeedback) return
    setHasClickedFeedback(true)
    try {
      logger?.info('summary_fb_thumbs_up')
    } catch (error) {
      console.error('Failed to log Thumbs Up:', error)
      toastError('Failed to report Thumbs Up action.')
    }
  }

  const handleThumbsDown = () => {
    toastSuccess('Thank you for your feedback!')
    if (hasClickedFeedback) {
      return
    }

    setHasClickedFeedback(true)

    try {
      logger?.info('summary_fb_thumbs_down')
    } catch (error) {
      console.error('Failed to log Thumbs Down:', error)
      toastError('Failed to report Thumbs Down action.')
    }
  }

  const handleCopySummary = async () => {
    try {
      if (summaryText) {
        copyToClipboard(summaryText)
      } else {
        toastError('No summary to copy.')
      }
    } catch (error) {
      console.error('Failed to copy text: ', error)
    }
  }

  return (
    <Flex
      gap="0.5rem"
      width="full"
      flexDirection="column"
      position="relative"
      _hover={{ '.quick-action-buttons': { display: 'flex' } }}
    >
      {isLoading ? (
        <>
          <Skeleton h="15rem" />
          <Flex w="full" h="3rem" gap="1.25rem">
            <Skeleton flexGrow="1" />
            <Skeleton flexGrow="1" />
          </Flex>
        </>
      ) : (
        <>
          <Flex
            justifyContent="space-between"
            textColor="gray.1300"
            textStyle={textStyleKeys.Body1_Bold}
            marginBottom="0.3rem"
          >
            Summary
            {hasSummary && (
              <Flex
                alignItems="center"
                className="quick-action-buttons"
                display="none"
                position="absolute"
                right="0"
                gap="0.5rem"
              >
                <QuickActionButton
                  variant={QuickActionButtonVariants.Default_100}
                  size={Size.sm}
                  actionIcon={iconCopy}
                  onClick={handleCopySummary}
                  tooltipText="Copy to clipboard"
                />
                <QuickActionButton
                  variant={QuickActionButtonVariants.Default_100}
                  size={Size.sm}
                  actionIcon={iconThumbsUp}
                  onClick={handleThumbsUp}
                  tooltipText="Accurate"
                />
                <QuickActionButton
                  variant={QuickActionButtonVariants.Default_100}
                  size={Size.sm}
                  actionIcon={iconThumbsDown}
                  tooltipText="Inaccurate"
                  onClick={handleThumbsDown}
                />
              </Flex>
            )}
          </Flex>
          <Flex
            textColor={
              callInstance?.transcription_status === 'IN_PROGRESS'
                ? 'gray.700'
                : 'gray.1300'
            }
            textStyle={textStyleKeys.Body1}
          >
            {summaryText && callInstance?.transcription_status !== 'IN_PROGRESS'
              ? summaryText
              : callInstance?.transcription_status === 'IN_PROGRESS'
                ? 'Processing call transcription...'
                : 'No Summary Found'}
          </Flex>
          {(voicemail || voicemailError) && (
            <VoicemailTranscriptSection
              voicemail={voicemail}
              voicemailError={voicemailError}
              isVoicemailLoading={isVoicemailLoading}
              onVoicemailClick={onVoicemailClick}
            />
          )}

          {callInstance?.transcription_status !== 'IN_PROGRESS' && (
            <>
              <Flex
                textColor="gray.1300"
                textStyle={textStyleKeys.Body1_Bold}
                mt="0.5rem"
              >
                Sentiment
              </Flex>

              <Flex gap="1.75rem">
                <OverallSentimentStatus
                  overallSentimentScore={overallCustomerScore}
                  personType="Patient"
                />
                <OverallSentimentStatus
                  overallSentimentScore={overallAgentScore}
                  personType="Office"
                />
              </Flex>
            </>
          )}
        </>
      )}
    </Flex>
  )
}
